// /hooks/orders/useCustomerOrders.ts
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  useListMyOrders,
  useCancelMyOrderItem,
  MyOrder,
  MyOrderItem,
  useUpdateMyOrder,
} from '../../features/customer-orders/api';
import { useModal } from '../useModal';
import { useListAddresses } from '../../features/addresses/api';
import { useSortableColumns } from '../sorting/useSortableColumns';
import { useDebouncedSearch } from '../useDebouncedSearch';

export interface FlattenedOrderItem extends MyOrderItem {
  loadNumber?: string | null;
  orderId: string;
  createdAt: string;
  deliveryAddressId?: string;
  poNumber?: string | null;
  userId: string;
  /** Unread org notes on the parent order. Notes are per order, so every row of
   * the same order carries the same count. */
  unseenNoteCount: number;
}

type UpdateOrderContext = {
  orderId: string;
  status: string;
  currentDeliveryAddressId?: string;
  poNumber?: string | null;
  loadNumber?: string | null;
};

export function useCustomerOrders() {
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [productId, setProductId] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { sortBy, sortDirection, handleSort, clearSort } = useSortableColumns({
    defaultSortBy: 'date',
    defaultDirection: 'desc',
  });

  const debouncedSearch = useDebouncedSearch(search, 350);

  const { data, isLoading, isFetching } = useListMyOrders({
    status,
    search: debouncedSearch,
    productId,
    page,
    pageSize,
    sortBy: sortBy || undefined,
    sortDir: sortDirection,
  });

  const cancelItem = useCancelMyOrderItem();

  const cancelModal = useModal<{ orderId: string; itemId: string }>();
  const notesModal = useModal<FlattenedOrderItem>();

  // NEW: Update modal (address + PO + notes)
  const updateModal = useModal<UpdateOrderContext>();
  const displayModel = useModal<{ orderId: string }>();

  const orders = data?.orders ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const flattenedOrders: FlattenedOrderItem[] = orders.flatMap((order: MyOrder) =>
    order.items.map((item) => ({
      ...item,
      orderId: order.id,
      createdAt: order.createdAt,
      deliveryAddressId: order.deliveryAddressId || order.deliveryAddress?.id,
      poNumber: (order as any).poNumber ?? null, // NEW (type may not include it yet)
      userId: order.userId,
      unseenNoteCount: order.unseenNoteCount ?? 0,
    }))
  );

  useEffect(() => {
    setPage(1);
  }, [status, debouncedSearch, productId, pageSize]);

  const handleCancelRequest = (orderId: string, itemId: string) => {
    cancelModal.open({ orderId, itemId });
  };

  const handleShowOrder = (orderId: string) => {
    displayModel.open({ orderId });
  };

  const handleCancelConfirm = async (reason?: string) => {
    if (!cancelModal.data) return;
    try {
      await cancelItem.mutateAsync({
        ...cancelModal.data,
        reason: reason || undefined,
      });
      toast.success('Item cancelled.');
      cancelModal.close();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Could not cancel item.');
    }
  };

  // Addresses for update modal
  const { data: addresses = [] } = useListAddresses();

  // NEW: one mutation that updates order fields (deliveryAddressId + poNumber)
  const updateOrder = useUpdateMyOrder();

  const handleOpenUpdate = (row: FlattenedOrderItem) => {
    updateModal.open({
      orderId: row.orderId,
      status: row.status,
      currentDeliveryAddressId: row.deliveryAddressId,
      poNumber: row.poNumber ?? null,
      loadNumber: row.loadNumber,
    });
  };

  const handleUpdateConfirm = async (payload: {
    orderId: string;
    deliveryAddressId?: string;
    poNumber?: string | null;
  }) => {
    try {
      await updateOrder.mutateAsync(payload);
      toast.success('Order updated.');
      updateModal.close();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Could not update order.');
    }
  };

  return {
    flattenedOrders,
    total,
    totalPages,
    isLoading,
    isFetching,

    status,
    search,
    productId,
    page,
    pageSize,
    setStatus,
    setSearch,
    setProductId,
    setPage,
    setPageSize,
    sortBy,
    sortDirection,
    handleSort,
    clearSort,

    cancelModal,
    notesModal,

    // NEW
    updateModal,
    addresses,
    isUpdatingOrder: updateOrder.isPending,
    handleOpenUpdate,
    handleUpdateConfirm,

    handleCancelRequest,
    handleCancelConfirm,

    cancelItem,
    displayModel,
    handleShowOrder,
  };
}
